// src/components/AppComponents/Sidebar/DropdownUserProfile.tsx

import { useEffect, useState } from 'react';
import { useUserSettingsStore } from '@/domains/user/userSettings.store';
import { useSyncLightMode } from '@/shared/hooks/useSyncLightMode';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSubMenu,
  DropdownMenuSubMenuContent,
  DropdownMenuSubMenuTrigger,
  DropdownMenuTrigger,
} from '@UI/components/DropdownMenu';
// import { useUserProfileStore } from '@/domains/user/userProfile.store';
import { IUserProfile, TLightMode } from '@/domains/user/user.types';
import i18n from '@/shared/i18n/i18n';
import { RiComputerLine, RiMoonLine, RiSunLine } from '@remixicon/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLogOutUseCase } from '@/domains/auth/auth.usecases';
import Gleap from 'gleap';
import { clearSentryUser } from '@/shared/lib/sentry';
import { clearAllStores } from '@/shared/lib/storesRegistry';
import { NAVBAR_SOURCES } from './NavBar/NavBarSources';

export interface DropdownUserProfileProps {
  user: IUserProfile;
  children: React.ReactNode;
  align?: 'start' | 'center' | 'end';
}

const SHOW_LIGHT_MODE = import.meta.env.VITE_SHOW_LIGHTMODE === 'true';

const DropdownUserProfile: React.FC<DropdownUserProfileProps> = ({
  user,
  children,
  align = 'start',
}) => {
  const lightMode = useUserSettingsStore((state) => state.settings.light_mode);
  const toggleLightMode = useUserSettingsStore(
    (state) => state.toggleLightMode,
  );
  useSyncLightMode();

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const logout = useLogOutUseCase();
  const queryClient = useQueryClient();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error logging out:', error);
    } finally {
      await queryClient.cancelQueries();
      queryClient.clear();
      Gleap.clearIdentity();
      clearSentryUser();
      clearAllStores();
      navigate(NAVBAR_SOURCES.login, { replace: true });
    }
  };

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return null;
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{children}</DropdownMenuTrigger>
      <DropdownMenuContent
        align={align}
        className="!min-w-[calc(var(--radix-dropdown-menu-trigger-width))]"
      >
        <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
        {SHOW_LIGHT_MODE && (
          <>
            <DropdownMenuGroup>
              <DropdownMenuSubMenu>
                <DropdownMenuSubMenuTrigger>
                  {i18n.t('navBar.lightMode')}
                </DropdownMenuSubMenuTrigger>
                <DropdownMenuSubMenuContent>
                  <DropdownMenuRadioGroup
                    value={lightMode}
                    onValueChange={(value: string) =>
                      toggleLightMode(value as TLightMode)
                    }
                  >
                    <DropdownMenuRadioItem
                      aria-label="Switch to Light Mode"
                      value="light"
                      iconType="check"
                    >
                      <RiSunLine
                        className="size-4 shrink-0"
                        aria-hidden="true"
                      />
                      {i18n.t('navBar.light')}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      aria-label="Switch to Dark Mode"
                      value="dark"
                      iconType="check"
                    >
                      <RiMoonLine
                        className="size-4 shrink-0"
                        aria-hidden="true"
                      />
                      {i18n.t('navBar.dark')}
                    </DropdownMenuRadioItem>
                    <DropdownMenuRadioItem
                      aria-label="Switch to System Mode"
                      value="system"
                      iconType="check"
                    >
                      <RiComputerLine
                        className="size-4 shrink-0"
                        aria-hidden="true"
                      />
                      {i18n.t('navBar.system')}
                    </DropdownMenuRadioItem>
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubMenuContent>
              </DropdownMenuSubMenu>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          className={
            pathname === NAVBAR_SOURCES.settings
              ? 'text-revving-brand dark:text-darkRevving-brand'
              : ''
          }
          onSelect={() => navigate(NAVBAR_SOURCES.settings)}
        >
          {i18n.t('navBar.settings')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={handleLogout}>
          {i18n.t('navBar.logout')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default DropdownUserProfile;
